import { type ModuleId, TIERS, type TierConfig, MODULES } from '../content';

// System Groups
export type GroupId = 6 | 7 | 8;
export const GROUPS: GroupId[] = [6, 7, 8];

/**
 * De ENIGE plek waar een groepsnummer een label wordt.
 *
 * Tot 12 aug 2026 stond `` `Groep ${grade}` `` op vijf plekken los in het
 * adminportaal, en het bewerkformulier had daarnaast een eigen hardgecodeerde
 * lijst van drie opties. Numeriek klopte dat allemaal — maar zou er ooit een
 * vierde groep bijkomen (een pilot in groep 5 bijvoorbeeld), dan groeit `GROUPS`
 * en blijft dat ene formulier stil achter. Dat is het soort fout dat pas opvalt
 * als een beheerder een leerling niet in de juiste groep kan zetten.
 */
export function formatGroupLabel(grade: number | undefined | null): string | undefined {
  if (grade === undefined || grade === null) return undefined;
  return `Groep ${grade}`;
}

/** Klaar voor een `<Select options={...}>`, zodat een formulier nooit zijn eigen groepenlijst bijhoudt. */
export const GROUP_OPTIONS = GROUPS.map(g => ({
  value: String(g),
  label: `Groep ${g}`
}));

// Starting Period — when the student stepped into their group
export type StartingPeriod = 'B' | 'M' | 'E';
export const STARTING_PERIODS: {
  id: StartingPeriod;
  label: string;
  description: string;
}[] = [{
  id: 'B',
  label: 'Begin',
  description: 'Start van het schooljaar'
}, {
  id: 'M',
  label: 'Midden',
  description: 'Halverwege het schooljaar'
}, {
  id: 'E',
  label: 'Eind',
  description: 'Einde van het schooljaar'
}];

/**
 * Get the starting tier for a group + period combination.
 * Maps to the first (non-plus) tier of that period.
 * E.g., Group 7 + Period M → M7 (tier 9)
 */
export function getStartingLevelForPeriod(group: GroupId, period: StartingPeriod): number {
  const match = TIERS.find(t => t.group === group && t.period === period && !t.isPlus);
  // Fallback to first tier of group
  return match?.tier ?? TIERS.find(t => t.group === group)?.tier ?? 1;
}

// Configuration Interface
export interface SystemConfig {
  groups: GroupId[];
  modules: typeof MODULES;
  tiers: TierConfig[];
  version: string;
}

// Centralized System Configuration
export const SYSTEM_CONFIG: SystemConfig = {
  groups: GROUPS,
  modules: MODULES,
  tiers: TIERS,
  version: '3.0.0'
};

/** ISO date string of last build/deploy. Update on each release. */
export const BUILD_DATE = '2026-04-21';

// Helper to get tiers for a specific group
export const getTiersForGroup = (group: GroupId): TierConfig[] => {
  return TIERS.filter(tier => tier.group === group);
};

// ─── Storage Quota Configuration ──────────────────────────────────────────────
// §6.2 — Centralised quota thresholds (consumed by storageQuota.ts)

export const STORAGE_QUOTA_CONFIG = {
  /** Warn user when localStorage exceeds this (bytes) */
  warningThresholdBytes: 4 * 1024 * 1024,
  // 4 MB
  /** Hard limit — start evicting old data (bytes) */
  criticalThresholdBytes: 4.5 * 1024 * 1024,
  // 4.5 MB
  /** Max age for attempt history before LRU eviction (days) */
  attemptHistoryMaxAgeDays: 90
};